import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Input,
  Text,
  FormLabel,
  FormControl,
  FormErrorMessage,
  useToast,
  VStack,
  HStack,
  IconButton,
  Badge,
  Box,
  useColorModeValue,
  Divider,
  Spinner,
} from "@chakra-ui/react";
import { AddIcon, CloseIcon } from "@chakra-ui/icons";
import { hitBeApi } from "../../api/api";

const EMAIL_RE =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

/**
 * Normalize phone for backend (digits + optional leading +).
 * Requires 8–15 digits total (reasonable E.164 range).
 * @param {string} raw
 * @returns {string|null}
 */
export function normalizeInvitePhone(raw) {
  const t = String(raw || "").trim();
  if (!t) return null;
  const spaced = t.replace(/\s+/g, " ").trim();
  const hasPlus = spaced.startsWith("+");
  const digits = spaced.replace(/\D/g, "");
  if (digits.length < 8 || digits.length > 15) return null;
  return hasPlus ? `+${digits}` : digits;
}

/**
 * @param {string} value — single trimmed candidate
 * @returns {{ status: 'empty' | 'partial' | 'email' | 'phone' | 'invalid', normalized?: string, message?: string, label?: string }}
 */
export function classifyInviteEntry(value) {
  const v = String(value || "").trim();
  if (!v) {
    return { status: "empty" };
  }

  const looksLikeEmail =
    v.includes("@") ||
    EMAIL_RE.test(v) ||
    /^[^\s@]+@[^\s@]*$/.test(v);

  if (looksLikeEmail) {
    if (EMAIL_RE.test(v)) {
      return {
        status: "email",
        normalized: v.toLowerCase(),
        label: "Email",
      };
    }
    return {
      status: "partial",
      message:
        !v.includes("@")
          ? "Complete the email address (include @ and domain)."
          : "Finish the domain (e.g. name@example.com).",
      label: "Email?",
    };
  }

  const seenDigit = /\d/.test(v);
  if (!seenDigit) {
    return {
      status: "invalid",
      message: "Enter a valid email address, or a phone number with digits (use country code, e.g. +91 …).",
    };
  }

  const normalized = normalizeInvitePhone(v);
  if (normalized) {
    return {
      status: "phone",
      normalized,
      label: "Phone",
    };
  }

  return {
    status: "invalid",
    message:
      "Use a valid phone number: 8–15 digits with optional + (spaces allowed). Examples: +91 9876543210, 9876543210.",
  };
}

/** @deprecated Prefer per-row classification; kept for callers that split bulk text */
export function parseInvitationContacts(raw) {
  const emails = [];
  const phones = [];
  const seenE = new Set();
  const seenP = new Set();

  String(raw || "")
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .forEach((chunk) => {
      const r = classifyInviteEntry(chunk);
      if (r.status === "email" && r.normalized && !seenE.has(r.normalized)) {
        seenE.add(r.normalized);
        emails.push(r.normalized);
      }
      if (r.status === "phone" && r.normalized && !seenP.has(r.normalized)) {
        seenP.add(r.normalized);
        phones.push(r.normalized);
      }
    });

  return { emails, phones };
}

function newRowId() {
  return `row-${
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`
  }`;
}

export default function ShareCollaborationModal({
  isOpen,
  onClose,
  titleLabel,
  kind,
  anchor = {},
}) {
  const toast = useToast();
  const [rows, setRows] = useState([{ id: newRowId(), value: "" }]);
  const [touchedSubmit, setTouchedSubmit] = useState(false);
  const [blurredRowIds, setBlurredRowIds] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessOverview, setAccessOverview] = useState(null);
  const [accessError, setAccessError] = useState(null);
  const [retryDeliveryId, setRetryDeliveryId] = useState(null);
  const [revokeBusy, setRevokeBusy] = useState(false);

  const muted = useColorModeValue("gray.600", "gray.400");
  const inputBg = useColorModeValue("white", "gray.800");
  const manageBorder = useColorModeValue("gray.200", "gray.600");
  const managePanelBg = useColorModeValue("gray.50", "whiteAlpha.50");

  const anchorKey =
    anchor.categoryAnchorId || anchor.todoAnchorId || anchor.dailyChoreAnchorId;

  const loadAccessOverview = useCallback(async () => {
    if (!kind || !anchorKey) return;
    setAccessLoading(true);
    setAccessError(null);
    try {
      const params = new URLSearchParams({ kind, _: String(Date.now()) });
      if (anchor.categoryAnchorId) {
        params.set("categoryAnchorId", String(anchor.categoryAnchorId));
      }
      if (anchor.todoAnchorId) {
        params.set("todoAnchorId", String(anchor.todoAnchorId));
      }
      if (anchor.dailyChoreAnchorId) {
        params.set("dailyChoreAnchorId", String(anchor.dailyChoreAnchorId));
      }
      const res = await hitBeApi(`collaboration/access?${params.toString()}`, "GET");
      if (res?.success) {
        setAccessOverview(res.data || null);
        setAccessError(null);
      } else {
        setAccessOverview(null);
        setAccessError(res?.message || "Could not load sharing details.");
      }
    } catch (e) {
      setAccessOverview(null);
      setAccessError(e?.message || "Could not load sharing details.");
    } finally {
      setAccessLoading(false);
    }
  }, [
    kind,
    anchorKey,
    anchor.categoryAnchorId,
    anchor.todoAnchorId,
    anchor.dailyChoreAnchorId,
  ]);

  useEffect(() => {
    if (!isOpen) {
      setRows([{ id: newRowId(), value: "" }]);
      setTouchedSubmit(false);
      setBlurredRowIds(new Set());
      setAccessOverview(null);
      setAccessError(null);
      setRetryDeliveryId(null);
      return;
    }
    if (anchorKey && kind) {
      loadAccessOverview();
    }
  }, [isOpen, anchorKey, kind, loadAccessOverview]);

  const markRowBlurred = useCallback((id) => {
    setBlurredRowIds((prev) => new Set(prev).add(id));
  }, []);

  const rowMeta = useMemo(
    () =>
      rows.map((r) => ({
        ...r,
        classification: classifyInviteEntry(r.value),
      })),
    [rows]
  );

  const setRowValue = useCallback((id, value) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, value } : r))
    );
  }, []);

  const addRow = useCallback(() => {
    setRows((prev) => [...prev, { id: newRowId(), value: "" }]);
  }, []);

  const removeRow = useCallback((id) => {
    setRows((prev) => {
      if (prev.length <= 1) {
        return [{ id: prev[0].id, value: "" }];
      }
      return prev.filter((r) => r.id !== id);
    });
  }, []);

  const handleSubmit = async () => {
    setTouchedSubmit(true);

    if (!inviteFormEnabled) {
      toast({
        title: "Invite form unavailable",
        description:
          accessError ||
          "You do not have permission to send invites here, or details are still loading.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!kind || !anchorKey) {
      toast({
        title: "Nothing to share",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const emails = [];
    const phones = [];
    const seenE = new Set();
    const seenP = new Set();
    let hasAnyInput = false;
    let hasInvalid = false;

    for (const { value } of rows) {
      const v = String(value || "").trim();
      if (!v) continue;
      hasAnyInput = true;
      const c = classifyInviteEntry(v);
      if (c.status === "invalid" || c.status === "partial") {
        hasInvalid = true;
        continue;
      }
      if (c.status === "email" && c.normalized && !seenE.has(c.normalized)) {
        seenE.add(c.normalized);
        emails.push(c.normalized);
      }
      if (c.status === "phone" && c.normalized && !seenP.has(c.normalized)) {
        seenP.add(c.normalized);
        phones.push(c.normalized);
      }
    }

    if (!hasAnyInput) {
      toast({
        title: "Add at least one contact",
        description: "Use one row per email or phone number.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    if (hasInvalid) {
      toast({
        title: "Fix highlighted fields",
        description: "Each line must be a complete email address or a valid phone number.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!emails.length && !phones.length) {
      toast({
        title: "Nothing valid to send",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setSubmitting(true);
    try {
      const body = {
        kind,
        emails,
        phones,
      };
      if (anchor.categoryAnchorId) body.categoryAnchorId = anchor.categoryAnchorId;
      if (anchor.todoAnchorId) body.todoAnchorId = anchor.todoAnchorId;
      if (anchor.dailyChoreAnchorId)
        body.dailyChoreAnchorId = anchor.dailyChoreAnchorId;

      const res = await hitBeApi("collaboration/invite", "POST", body);
      if (!res?.success) {
        throw new Error(res?.message || "Invite failed");
      }
      const pending = res.data?.pending?.length ?? 0;
      const added = res.data?.addedUserIds?.length ?? 0;
      toast({
        title: "Invites sent",
        description:
          added || pending
            ? `${added} collaborator(s) added on this app. ${pending} invite(s) emailed or texted.`
            : "Processed (no new invites — check invalid lines or existing members).",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      if (res.data?.errors?.length) {
        toast({
          title: "Some invites failed",
          description: res.data.errors
            .slice(0, 3)
            .map((e) => `${e.channel}: ${e.message}`)
            .join(" · "),
          status: "warning",
          duration: 6000,
          isClosable: true,
        });
      }
      await loadAccessOverview();
      setRows([{ id: newRowId(), value: "" }]);
    } catch (e) {
      toast({
        title: "Could not send invites",
        description: e?.message || "Try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const buildRevokeBody = (extra) => {
    const body = { kind, ...extra };
    if (anchor.categoryAnchorId) body.categoryAnchorId = anchor.categoryAnchorId;
    if (anchor.todoAnchorId) body.todoAnchorId = anchor.todoAnchorId;
    if (anchor.dailyChoreAnchorId) {
      body.dailyChoreAnchorId = anchor.dailyChoreAnchorId;
    }
    return body;
  };

  const handleRemoveCollaborator = async (participantUserId) => {
    if (!participantUserId || revokeBusy) return;
    setRevokeBusy(true);
    try {
      const res = await hitBeApi(
        "collaboration/revoke",
        "POST",
        buildRevokeBody({ action: "participant", participantUserId })
      );
      if (!res?.success) {
        throw new Error(res?.message || "Could not remove access.");
      }
      toast({ title: "Access removed", status: "success", duration: 3000, isClosable: true });
      await loadAccessOverview();
    } catch (e) {
      toast({
        title: "Revoke failed",
        description: e?.message || "Try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setRevokeBusy(false);
    }
  };

  const handleCancelPending = async (inv) => {
    if (revokeBusy) return;
    setRevokeBusy(true);
    try {
      const res = await hitBeApi(
        "collaboration/revoke",
        "POST",
        buildRevokeBody({
          action: "pending_invite",
          pendingEmail: inv.normalizedEmail || undefined,
          pendingPhone: inv.normalizedPhone || undefined,
        })
      );
      if (!res?.success) {
        throw new Error(res?.message || "Could not cancel invite.");
      }
      toast({ title: "Invite cancelled", status: "success", duration: 3000, isClosable: true });
      await loadAccessOverview();
    } catch (e) {
      toast({
        title: "Could not cancel invite",
        description: e?.message || "Try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setRevokeBusy(false);
    }
  };

  const handleDeactivateAll = async () => {
    if (
      revokeBusy ||
      !window.confirm(
        "Remove every collaborator and cancel all pending invites for this item? You can share again later."
      )
    ) {
      return;
    }
    setRevokeBusy(true);
    try {
      const res = await hitBeApi(
        "collaboration/revoke",
        "POST",
        buildRevokeBody({ action: "deactivate_all" })
      );
      if (!res?.success) {
        throw new Error(res?.message || "Could not clear sharing.");
      }
      toast({
        title: "Sharing cleared",
        description:
          "All collaborators and pending invites were removed from this shared item.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      await loadAccessOverview();
    } catch (e) {
      toast({
        title: "Could not clear sharing",
        description: e?.message || "Try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setRevokeBusy(false);
    }
  };

  const buildAnchoredBody = (extra = {}) => {
    const body = { kind, ...extra };
    if (anchor.categoryAnchorId) body.categoryAnchorId = anchor.categoryAnchorId;
    if (anchor.todoAnchorId) body.todoAnchorId = anchor.todoAnchorId;
    if (anchor.dailyChoreAnchorId) body.dailyChoreAnchorId = anchor.dailyChoreAnchorId;
    return body;
  };

  const handlePromoteAdmin = async (participantUserId) => {
    if (!participantUserId || revokeBusy) return;
    setRevokeBusy(true);
    try {
      const res = await hitBeApi(
        "collaboration/revoke",
        "POST",
        buildRevokeBody({ action: "promote_admin", participantUserId })
      );
      if (!res?.success) {
        throw new Error(res?.message || "Could not promote.");
      }
      toast({
        title: "Sharing admin",
        description: "They can invite others to this shared item.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      await loadAccessOverview();
    } catch (e) {
      toast({
        title: "Could not update role",
        description: e?.message || "Try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setRevokeBusy(false);
    }
  };

  const handleDemoteAdmin = async (participantUserId) => {
    if (!participantUserId || revokeBusy) return;
    setRevokeBusy(true);
    try {
      const res = await hitBeApi(
        "collaboration/revoke",
        "POST",
        buildRevokeBody({ action: "demote_admin", participantUserId })
      );
      if (!res?.success) {
        throw new Error(res?.message || "Could not demote.");
      }
      toast({ title: "Admin role removed", status: "success", duration: 3000, isClosable: true });
      await loadAccessOverview();
    } catch (e) {
      toast({
        title: "Could not update role",
        description: e?.message || "Try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setRevokeBusy(false);
    }
  };

  const handleRetryFailedDelivery = async (deliveryId) => {
    if (!deliveryId || retryDeliveryId) return;
    setRetryDeliveryId(deliveryId);
    try {
      const res = await hitBeApi(
        "collaboration/retry-failed-invite",
        "POST",
        buildAnchoredBody({ deliveryId })
      );
      if (!res?.success) {
        throw new Error(res?.message || "Retry failed.");
      }
      toast({
        title: "Invite sent",
        description: "Delivery succeeded — invite is pending for the recipient.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      await loadAccessOverview();
    } catch (e) {
      toast({
        title: "Retry failed",
        description: e?.message || "Try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      await loadAccessOverview();
    } finally {
      setRetryDeliveryId(null);
    }
  };

  const collaborators = accessOverview?.collaborators || [];
  const pendingList = accessOverview?.pendingInvites || [];
  const failedDeliveries = accessOverview?.failedInviteDeliveries || [];
  const isWorkspaceOwnerUI = !!accessOverview?.isWorkspaceOwner;
  const isViewerCollabAdmin = !!accessOverview?.isCollaborationAdminRole;
  const inviteFormEnabled =
    !accessLoading &&
    !accessError &&
    !!accessOverview?.canManageInvites;
  const hasManageSection = !!anchorKey && !!kind;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius="xl">
        <ModalHeader pr={14}>{titleLabel}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            {hasManageSection ? (
              <Box
                borderWidth="1px"
                borderColor={manageBorder}
                borderRadius="lg"
                p={4}
                bg={managePanelBg}
              >
                {accessError ? (
                  <Text fontSize="sm" color="red.500">
                    {accessError}
                  </Text>
                ) : null}
                <Text fontSize="sm" fontWeight="semibold" mb={2}>
                  People with access
                </Text>
                {accessLoading ? (
                  <HStack spacing={2} py={2}>
                    <Spinner size="sm" />
                    <Text fontSize="sm" color={muted}>
                      Loading…
                    </Text>
                  </HStack>
                ) : collaborators.length === 0 &&
                  pendingList.length === 0 &&
                  failedDeliveries.length === 0 ? (
                  <Text fontSize="sm" color={muted}>
                    Nobody else yet — invites you send will appear here.
                  </Text>
                ) : (
                  <VStack align="stretch" spacing={3}>
                    {collaborators.map((c) => {
                      const showRemove =
                        isWorkspaceOwnerUI ||
                        (isViewerCollabAdmin && !c.isCollaborationAdmin);
                      return (
                        <HStack
                          key={c.userId}
                          justify="space-between"
                          align="flex-start"
                          gap={2}
                          flexWrap="wrap"
                        >
                          <Box minW={0} flex="1">
                            <HStack spacing={2} align="center">
                              <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                                {c.name || "(No name)"}
                              </Text>
                              {c.isCollaborationAdmin ? (
                                <Badge colorScheme="purple" fontSize="10px">
                                  Sharing admin
                                </Badge>
                              ) : (
                                <Badge colorScheme="gray" fontSize="10px">
                                  Member
                                </Badge>
                              )}
                            </HStack>
                            <Text fontSize="xs" color={muted} noOfLines={2}>
                              {c.email || c.phoneNumber || c.userId}
                            </Text>
                            {isWorkspaceOwnerUI ? (
                              <HStack spacing={2} mt={2} flexWrap="wrap">
                                {!c.isCollaborationAdmin ? (
                                  <Button
                                    size="xs"
                                    variant="outline"
                                    colorScheme="purple"
                                    borderRadius="md"
                                    isDisabled={revokeBusy || accessLoading}
                                    onClick={() => handlePromoteAdmin(c.userId)}
                                  >
                                    Make sharing admin
                                  </Button>
                                ) : (
                                  <Button
                                    size="xs"
                                    variant="ghost"
                                    borderRadius="md"
                                    isDisabled={revokeBusy || accessLoading}
                                    onClick={() => handleDemoteAdmin(c.userId)}
                                  >
                                    Remove admin
                                  </Button>
                                )}
                              </HStack>
                            ) : null}
                          </Box>
                          {showRemove ? (
                            <Button
                              size="xs"
                              variant="outline"
                              colorScheme="red"
                              borderRadius="md"
                              isDisabled={revokeBusy || accessLoading}
                              onClick={() => handleRemoveCollaborator(c.userId)}
                            >
                              Remove
                            </Button>
                          ) : null}
                        </HStack>
                      );
                    })}
                    {pendingList.map((inv, idx) => {
                      const label =
                        inv.normalizedEmail ||
                        inv.normalizedPhone ||
                        `Pending ${idx + 1}`;
                      return (
                        <HStack
                          key={`${label}-${inv.expiresAt || idx}`}
                          justify="space-between"
                          align="center"
                          gap={2}
                        >
                          <Text fontSize="sm" color={muted}>
                            Invite pending · {label}
                          </Text>
                          {isWorkspaceOwnerUI || isViewerCollabAdmin ? (
                            <Button
                              size="xs"
                              variant="ghost"
                              borderRadius="md"
                              isDisabled={revokeBusy || accessLoading}
                              onClick={() => handleCancelPending(inv)}
                            >
                              Cancel invite
                            </Button>
                          ) : null}
                        </HStack>
                      );
                    })}
                    {failedDeliveries.length > 0 ? (
                      <>
                        <Divider />
                        <Text fontSize="sm" fontWeight="semibold">
                          Invite delivery failed
                        </Text>
                        <Text fontSize="xs" color={muted}>
                          Someone has not received their email or SMS yet. Retry after fixing mail
                          / SMS setup.
                        </Text>
                        {failedDeliveries.map((f) => {
                          const target =
                            f.normalizedEmail || f.normalizedPhone || "(unknown)";
                          const msgPreview = String(f.message || "").slice(0, 160);
                          return (
                            <HStack
                              key={f.deliveryId}
                              align="flex-start"
                              gap={3}
                              flexWrap="wrap"
                              justify="space-between"
                            >
                              <Box minW={0} flex={1}>
                                <Text fontSize="sm">
                                  <Badge mr={2} colorScheme={f.channel === "email" ? "blue" : "orange"}>
                                    {f.channel}
                                  </Badge>
                                  {target}
                                </Text>
                                {msgPreview ? (
                                  <Text fontSize="xs" color={muted} mt={1}>
                                    {msgPreview}
                                    {(f.message || "").length > 160 ? "…" : ""}
                                  </Text>
                                ) : null}
                              </Box>
                              <Button
                                size="xs"
                                colorScheme="teal"
                                borderRadius="md"
                                isLoading={retryDeliveryId === f.deliveryId}
                                isDisabled={
                                  !!(retryDeliveryId && retryDeliveryId !== f.deliveryId) ||
                                  revokeBusy ||
                                  accessLoading
                                }
                                onClick={() => handleRetryFailedDelivery(f.deliveryId)}
                              >
                                Retry send
                              </Button>
                            </HStack>
                          );
                        })}
                      </>
                    ) : null}
                    {isWorkspaceOwnerUI ? (
                      <>
                        <Divider />
                        <Button
                          size="sm"
                          variant="outline"
                          colorScheme="orange"
                          borderRadius="lg"
                          isDisabled={
                            revokeBusy ||
                            accessLoading ||
                            (!collaborators.length && !pendingList.length && !failedDeliveries.length)
                          }
                          onClick={handleDeactivateAll}
                        >
                          Revoke everyone & clear invites
                        </Button>
                      </>
                    ) : null}
                  </VStack>
                )}
              </Box>
            ) : null}

            <Divider />

            {inviteFormEnabled ? (
              <>
                <Text fontSize="sm" color={muted}>
                  One field per contact. Valid entries show a green <strong>Email</strong> or{" "}
                  <strong>Phone</strong> label. Invitees already on Manage360 are added instantly;
                  others receive an email or SMS with your join link. Only workspace owners and
                  sharing admins can add people here.
                </Text>

                <Box>
                  <FormLabel fontSize="sm" fontWeight="semibold" mb={2}>
                    Contacts
                  </FormLabel>
              <VStack align="stretch" spacing={3}>
                {rowMeta.map((row) => {
                  const { classification: c } = row;
                  const trimmed = row.value.trim();
                  const hasText = trimmed.length > 0;
                  const blurred = blurredRowIds.has(row.id);

                  const showOk =
                    hasText &&
                    (c.status === "email" || c.status === "phone");

                  const incomplete =
                    hasText &&
                    (c.status === "invalid" || c.status === "partial");

                  /** Don’t nag while typing; show after blur or after Send invites */
                  const showInlineError =
                    incomplete &&
                    !showOk &&
                    (blurred || touchedSubmit);

                  const errorText =
                    c.message ||
                    "Enter a valid email or international phone number (8–15 digits, optional +).";

                  return (
                    <HStack key={row.id} align="flex-start" spacing={2}>
                      <FormControl flex={1} isInvalid={showInlineError}>
                        <Input
                          value={row.value}
                          size="md"
                          borderRadius="lg"
                          bg={inputBg}
                          placeholder="Email or phone (one per line)"
                          spellCheck={false}
                          autoComplete="off"
                          onChange={(e) => setRowValue(row.id, e.target.value)}
                          onBlur={() => markRowBlurred(row.id)}
                          aria-invalid={showInlineError}
                          aria-describedby={showInlineError ? `${row.id}-err` : undefined}
                        />
                        {showInlineError ? (
                          <FormErrorMessage fontSize="xs" id={`${row.id}-err`}>
                            {errorText}
                          </FormErrorMessage>
                        ) : null}
                      </FormControl>

                      <HStack
                        spacing={1}
                        flexShrink={0}
                        pt={showInlineError ? "0px" : "8px"}
                        alignItems={showInlineError ? "flex-start" : "center"}
                      >
                        <Box minW={{ base: "64px", sm: "74px" }} textAlign="center">
                          {showOk ? (
                            <Badge
                              colorScheme="green"
                              variant="solid"
                              borderRadius="full"
                              px={2}
                              py={0.5}
                              fontSize="10px"
                              textTransform="uppercase"
                              letterSpacing="0.04em"
                            >
                              {c.label}
                            </Badge>
                          ) : (
                            <Box minW="1px" minH="26px" />
                          )}
                        </Box>
                        <IconButton
                          aria-label={rows.length <= 1 ? "Clear row" : "Remove row"}
                          icon={<CloseIcon boxSize={3} />}
                          size="sm"
                          variant="ghost"
                          colorScheme="gray"
                          onClick={() =>
                            rows.length <= 1
                              ? setRowValue(row.id, "")
                              : removeRow(row.id)
                          }
                        />
                      </HStack>
                    </HStack>
                  );
                })}
              </VStack>

                  <Button
                    leftIcon={<AddIcon boxSize={3} />}
                    size="sm"
                    variant="outline"
                    colorScheme="teal"
                    borderRadius="lg"
                    mt={3}
                    onClick={addRow}
                    isDisabled={submitting}
                  >
                    Add another contact
                  </Button>
                </Box>
              </>
            ) : !accessLoading && accessError ? null : (
              <Text fontSize="sm" color={muted}>
                You can view who has access, but only the workspace owner or a sharing admin can send
                new invites.
              </Text>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter gap={2} flexWrap="wrap">
          <Button variant="ghost" onClick={onClose} isDisabled={submitting}>
            Cancel
          </Button>
          <Button
            colorScheme="teal"
            borderRadius="lg"
            onClick={handleSubmit}
            isLoading={submitting}
            loadingText="Sending"
            isDisabled={!anchorKey || !inviteFormEnabled}
          >
            Send invites
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
